import { onSnapshot } from "firebase/firestore";
import type {
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  FirestoreError,
  Query,
  QuerySnapshot,
  Unsubscribe,
} from "firebase/firestore";

/**
 * Self-healing Firestore snapshot listeners.
 *
 * Firestore tears a listener down permanently on error — it never retries on
 * its own, and with no error callback the SDK just logs "Uncaught Error in
 * snapshot listener" to the console. That leaves the page rendering stale
 * cached data with no live connection until a reload.
 *
 * The failure we actually hit is a `permission-denied` that is not a real
 * access problem. Writes here are fire-and-forget (see `offlineWrites.ts`), so
 * a just-created trip/segment/activity exists in the local cache before it
 * exists on the server. Any listener opened in that window is evaluated by
 * rules against a document that isn't there yet:
 *
 *   allow read: if ... request.auth.uid in resource.data.memberUserIds;   // resource == null
 *   allow read: if ... in get(/trips/$(tripId)).data.memberUserIds;       // get() == null
 *
 * Both error out, and a rule that errors evaluates as deny. Creating a trip and
 * navigating straight to it denied all three of its listeners (the trip doc,
 * its segments, its interactions). The same race reappears on reconnect, when
 * a queued offline write flushes at the same moment listeners re-target.
 *
 * Retrying a few times with backoff lets the listener recover as soon as the
 * write lands, and keeps the noise out of Sentry and the error toast. This is
 * deliberately not solved by awaiting the write before navigating: the app
 * boots with Firestore's network disabled (see `firebase.ts`) and only enables
 * it once a reachability probe passes, so a write promise can stay unresolved
 * indefinitely on flaky connections — awaiting one would hang the UI offline.
 *
 * A genuine denial (opening a trip you're not a member of) simply exhausts the
 * budget and is then reported once, ~3.75s later.
 */

/** Backoff between re-subscribe attempts after a `permission-denied`. */
export const PERMISSION_RETRY_DELAYS_MS = [250, 500, 1000, 2000];

export interface ResilientSnapshotFailure {
  error: FirestoreError;
  /**
   * Whether the last snapshot handed to the caller carried unsynced local
   * writes — i.e. this device created the document and the server hasn't taken
   * it yet.
   *
   * This is what separates the two reasons a listener gets denied. Our own
   * not-yet-synced document has pending writes, so cached data is still ours
   * to show. Cached data with no pending writes that the server refuses is
   * something we are simply not allowed to see any more — a trip we were
   * removed from, say — and the caller should stop rendering it.
   */
  hadPendingWrites: boolean;
}

export interface ResilientSnapshotOptions {
  /** Human-readable listener name, used when a failure is finally reported. */
  label: string;
  /**
   * Called when the listener fails for real: a non-recoverable error, or a
   * `permission-denied` that outlived the retry budget. Not called for the
   * transient denials that get retried.
   */
  onError?: (failure: ResilientSnapshotFailure) => void;
}

type AnySnapshot = DocumentSnapshot<DocumentData> | QuerySnapshot<DocumentData>;

// `onSnapshot` is heavily overloaded; the reference/query distinction is
// resolved at runtime by the SDK, so route both through one loose signature
// rather than duplicating the retry logic per overload.
const listen = onSnapshot as unknown as (
  target: unknown,
  onNext: (snapshot: AnySnapshot) => void,
  onError: (error: FirestoreError) => void,
) => Unsubscribe;

export function onSnapshotResilient<T = DocumentData>(
  reference: DocumentReference<T>,
  onNext: (snapshot: DocumentSnapshot<T>) => void,
  options: ResilientSnapshotOptions,
): Unsubscribe;
export function onSnapshotResilient<T = DocumentData>(
  query: Query<T>,
  onNext: (snapshot: QuerySnapshot<T>) => void,
  options: ResilientSnapshotOptions,
): Unsubscribe;
export function onSnapshotResilient(
  target: DocumentReference<DocumentData> | Query<DocumentData>,
  onNext: (snapshot: never) => void,
  { label, onError }: ResilientSnapshotOptions,
): Unsubscribe {
  const deliver = onNext as unknown as (snapshot: AnySnapshot) => void;
  let stopped = false;
  let attempt = 0;
  let hadPendingWrites = false;
  let unsubscribe: Unsubscribe | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  const subscribe = () => {
    if (stopped) return;

    unsubscribe = listen(
      target,
      (snapshot) => {
        // A server-backed snapshot proves the listener is healthy, so a later
        // denial gets a fresh budget. Cache-only snapshots prove nothing —
        // while the network is disabled every snapshot is `fromCache`.
        if (!snapshot.metadata.fromCache) attempt = 0;
        hadPendingWrites = snapshot.metadata.hasPendingWrites;
        deliver(snapshot);
      },
      (error) => {
        unsubscribe = null;

        if (
          error.code === "permission-denied" &&
          attempt < PERMISSION_RETRY_DELAYS_MS.length
        ) {
          const delay = PERMISSION_RETRY_DELAYS_MS[attempt];
          attempt += 1;
          retryTimer = setTimeout(() => {
            retryTimer = null;
            subscribe();
          }, delay);
          return;
        }

        // Out of budget, or something other than a denial: this one is real.
        // `console.error` is what feeds the in-app error toast and Sentry
        // (see `useErrorInterceptor`), matching how `fireWrite` reports.
        console.error(`Firestore listener failed (${label})`, error);
        onError?.({ error, hadPendingWrites });
      },
    );
  };

  subscribe();

  return () => {
    stopped = true;
    if (retryTimer) clearTimeout(retryTimer);
    unsubscribe?.();
  };
}
